import { DatePicker } from "components/antd-date-picker";
import { ProjectContext, TProjectContext } from "context/ProjectProvider";
import dayjs from "utils/dayjs-config";
import type { Dayjs } from "dayjs";
import "./style.scss";

import {
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
  useMemo,
  useRef
} from "react";

type DatePickerProps = {
  className?: string;
  popupClassName?: string;
  placeholder?: string;
  allowClear?: boolean;
  defaultOpen?: boolean;
  disabled?: boolean;
  format?: string;
  inputReadOnly?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  value?: Dayjs | null | undefined;
  onChange?:
    | ((date: dayjs.Dayjs | null, dateString: string | null) => void)
    | undefined;
  disabledDate?: (current: Dayjs) => boolean;
  renderExtraFooter?: any;
  style?: any;
  bordered?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
  placement?: "bottomLeft" | "bottomRight" | "topLeft" | "topRight";
  getPopupContainer?: any;
  showToday?: boolean;
  size?: "small" | "middle" | "large";
  id?: string;
  "data-testid"?: string;
};

const CIQDatePicker = forwardRef((props: DatePickerProps, ref) => {
  const dateRef: any = useRef();

  useImperativeHandle(ref, () => {
    return dateRef.current;
  });

  const {
    className = "h-0 w-0 p-0 m-0 border-0 border-none bg-white ciq-date-picker",
    popupClassName,
    placeholder,
    allowClear,
    defaultOpen,
    disabled,
    format,
    inputReadOnly,
    open,
    onOpenChange,
    value,
    onChange,
    disabledDate,
    renderExtraFooter,
    style,
    bordered = true,
    onFocus,
    onBlur,
    placement = "bottomLeft",
    getPopupContainer,
    showToday,
    size,
    id,
    "data-testid": dataTestId
  } = props;

  const { calHolidaysWeekends }: TProjectContext = useContext(ProjectContext);
  const holidays = calHolidaysWeekends?.holidays;
  const weekends = calHolidaysWeekends?.weekends;

  // Precompute a Set of project-zone day-keys ("YYYY-MM-DD") once per
  // holidays change, instead of running `dayjs.tz(hld.date)` inside the
  // per-cell disabled-date callback. With ~50 holidays × 42 cells per
  // month panel, the previous shape paid ~2 s of dayjs.tz parses on
  // every panel paint (CIQ-11205). Same project-zone semantics as the
  // CIQ-11176 fix: dayjs.tz(string) interprets naive "YYYY-MM-DD" via
  // dayjs.tz.setDefault(projectZone) set by ProjectProvider, so the
  // computed day-key matches the cell's day in project zone.
  const holidayDaySet = useMemo(() => {
    if (!holidays || holidays.length === 0) return null;
    return new Set(
      holidays
        .map((hld: any) =>
          hld?.date ? dayjs.tz(hld.date).format("YYYY-MM-DD") : null
        )
        .filter((s): s is string => !!s)
    );
  }, [holidays]);

  const weekendSet = useMemo(() => {
    if (!weekends || weekends.length === 0) return null;
    return new Set<number>(weekends);
  }, [weekends]);

  const checkDisablesDates = useCallback(
    (current: Dayjs) => {
      if (disabledDate && disabledDate(current)) return true;
      if (holidayDaySet?.has(current.format("YYYY-MM-DD"))) return true;
      if (weekendSet?.has(current.day())) return true;
      return false;
    },
    [holidayDaySet, weekendSet, disabledDate]
  );

  return (
    <DatePicker
      ref={dateRef}
      className={className}
      classNames={
        popupClassName ? { popup: { root: popupClassName } } : undefined
      }
      placeholder={placeholder}
      defaultOpen={defaultOpen}
      disabled={disabled}
      allowClear={allowClear}
      format={format}
      inputReadOnly={inputReadOnly}
      open={open}
      onOpenChange={onOpenChange}
      value={value}
      onChange={onChange}
      disabledDate={checkDisablesDates}
      renderExtraFooter={renderExtraFooter}
      style={style}
      variant={bordered ? "outlined" : "borderless"}
      onFocus={onFocus}
      onBlur={onBlur}
      placement={placement}
      getPopupContainer={getPopupContainer}
      showNow={showToday}
      size={size}
      id={id}
      data-testid={dataTestId}
    />
  );
});

CIQDatePicker.displayName = "CIQDatePicker";
export default CIQDatePicker;
